import { FC, useEffect, useRef, useState } from "react";
import { select, scaleLinear, max } from "d3";
import { Box, Typography, TypographyProps } from "@mui/material";
import EmptyComponent from "../EmptyComponent";

interface ScatterPlotDatum {
  x: number;
  y: number;
  label: string;
  frequency?: number;
  severity?: number;
  reasonName?: string;
  color?: string;
  category?: string;
}

interface ScatterPlotProps {
  data: ScatterPlotDatum[];
  width?: string | number;
  height?: string | number;
  xLabel?: string;
  yLabel?: string;
  title?: string;
  tooltipTitle?: string;
  titleProps?: TypographyProps;
}

const DEFAULT_MARGINS = { left: 60, right: 20, top: 30, bottom: 60 };

const ScatterPlot: FC<ScatterPlotProps> = ({
  data,
  width = "100%",
  height = "100%",
  xLabel = "Frequency (# of cases)",
  yLabel = "Severity (Avg mins)",
  title,
  tooltipTitle,
  titleProps,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<any>();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [tooltip, setTooltip] = useState<null | {
    x: number;
    y: number;
    datum: ScatterPlotDatum;
    pageX: number;
    pageY: number;
  }>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setDimensions({ width, height });
      }
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [data?.length]);

  useEffect(() => {
    if (!data?.length || !dimensions.width || !dimensions.height) return;
    const svg = select(svgRef.current);
    svg.selectAll("*").remove();
    const { left, right, top, bottom } = DEFAULT_MARGINS;
    const innerWidth = dimensions.width - left - right;
    const innerHeight = dimensions.height - top - bottom;

    // X and Y scales
    const xMax = max(data, (d) => d.x) ?? 100;
    const yMax = max(data, (d) => d.y) ?? 100;
    const xScale = scaleLinear()
      .domain([0, xMax * 1.1])
      .range([0, innerWidth])
      .nice();
    const yScale = scaleLinear()
      .domain([0, yMax * 1.1])
      .range([innerHeight, 0])
      .nice();

    const chart = svg
      .append("g")
      .attr("transform", `translate(${left},${top})`);

    // Remove default axes
    // Draw custom L-shaped axes
    // Remove default axis rendering
    // X axis (bottom)
    chart
      .append("line")
      .attr("x1", 0)
      .attr("y1", innerHeight)
      .attr("x2", innerWidth)
      .attr("y2", innerHeight)
      .attr("stroke", "#000000CC")
      .attr("stroke-width", 2)
      .attr("stroke-linecap", "square");
    // Y axis (left)
    chart
      .append("line")
      .attr("x1", 0)
      .attr("y1", innerHeight)
      .attr("x2", 0)
      .attr("y2", 0)
      .attr("stroke", "#000000CC")
      .attr("stroke-width", 2)
      .attr("stroke-linecap", "square");

    // X axis labels (bottom, skip last label)
    const xTicks = xScale.ticks(6).filter((d) => Number.isInteger(d));
    chart
      .selectAll(".custom-x-tick")
      .data(xTicks)
      .enter()
      .append("text")
      .attr("class", "custom-x-tick")
      .attr("x", (d) => xScale(d))
      .attr("y", innerHeight + 24)
      .attr("text-anchor", "middle")
      .attr("fill", "rgba(178, 178, 178, 1)")
      .style("font-size", 13)
      .text((d) => d);

    // Y axis labels (left, skip first label)
    const yTicks = yScale.ticks(6).filter((d) => Number.isInteger(d));
    chart
      .selectAll(".custom-y-tick")
      .data(yTicks)
      .enter()
      .append("text")
      .attr("class", "custom-y-tick")
      .attr("x", -10)
      .attr("y", (d) => yScale(d) + 4)
      .attr("text-anchor", "end")
      .attr("fill", "#00000066")
      .style("font-size", 13)
      .text((d) => d);

    // Remove all default axis rendering
    chart.selectAll(".domain, .tick").remove();

    // X label
    chart
      .append("text")
      .attr("x", innerWidth / 2)
      .attr("y", innerHeight + 50) // move further down
      .attr("text-anchor", "middle")
      .attr("fill", "#00000066")
      .style("font-size", 14)
      .text(xLabel);

    // Y label
    chart
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerHeight / 2)
      .attr("y", -50) // move further left
      .attr("text-anchor", "middle")
      .attr("fill", "#00000066")
      .style("font-size", 13)
      .text(yLabel);

    // --- FIX: Draw labels FIRST ---
    chart
      .selectAll("text.scatter-label")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "scatter-label")
      .attr("x", (d) => Math.min(xScale(d.x) + 12, innerWidth - 40))
      .attr("y", (d, i) => yScale(d.y) + (i % 2 === 0 ? -12 : 25))
      .attr("fill", "#000000")
      .style("font-size", 13)
      .text((d) => d.label);

    // --- Then draw circles ON TOP ---
    chart
      .selectAll("circle")
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", (d) => xScale(d.x))
      .attr("cy", (d) => yScale(d.y))
      .attr("r", 7)
      .attr("fill", (d) => d.color ?? "#9C27FF")
      .attr("opacity", 0.8)
      .style("cursor", "pointer")
      .on("mousemove", function (event, d) {
        setTooltip({
          x: xScale(d.x) + left,
          y: yScale(d.y) + top,
          datum: d,
          pageX: event.pageX,
          pageY: event.pageY,
        });
      })
      .on("mouseleave", function () {
        setTooltip(null);
      });
  }, [data, dimensions]);

  return (
    <Box
      flex={1}
      display="flex"
      flexDirection="column"
      sx={{ height: "100%", position: "relative" }}
    >
      {title && (
        <Typography
          variant="h6"
          color="custom.foreground.secondary"
          mb={3}
          sx={titleProps}
        >
          {title}
        </Typography>
      )}
      {data?.length > 0 ? (
        <Box
          ref={containerRef}
          flex={1}
          sx={{ width, height, position: "relative" }}
        >
          <svg ref={svgRef} style={{ width: "100%", height: "100%" }}></svg>
          {tooltip && (
            <Box
              sx={{
                position: "fixed",
                left: tooltip.pageX + 12,
                top: tooltip.pageY - 24,
                zIndex: 9999,
                bgcolor: "background.paper",
                border: "1px solid #000000CC",
                borderRadius: 1,
                boxShadow: 3,
                p: 1.5,
                minWidth: 180,
                pointerEvents: "none",
              }}
            >
              <Typography fontWeight={700} fontSize={15} mb={0.5}>
                {tooltip.datum.reasonName || tooltip.datum.label}
              </Typography>
              <Typography fontSize={13}>
                <b>Count:</b> {tooltip.datum.frequency ?? tooltip.datum.x}
              </Typography>
              <Typography fontSize={13}>
                <b>Avg. delay (min):</b>{" "}
                {tooltip.datum.severity ?? tooltip.datum.y}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <EmptyComponent />
      )}
    </Box>
  );
};

export default ScatterPlot;
